/**
 * Report Content Tab - Isolated UI and editing state for production performance
 *
 * Owns isEditing, isSaving, and the Edit Content button so clicking it only re-renders
 * this small subtree, not the entire ReportViewer (~2900 lines).
 */

import React, { useState, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { ReportContentEditArea, type ReportContentEditAreaRef, type ReportTemplateResponse } from './ReportContentEditArea';
import { Edit, Eye, Save, X as XIcon, Loader2, Trash2, RefreshCw, Target } from 'lucide-react';
import { useToastStore } from '../../stores/toast';
import { useV2RegenerationJobPoller } from '../../hooks/useV2RegenerationJobPoller';
import { FeedbackPlanPreviewModal, QAScoreReadinessPanel } from '../QA';
import { scoreReadiness, type ProposalEvaluationResponse } from '../../api/qaApi';

/** Matches "Based on N questions | Avg confidence: X% | Y unique sources" (same as ReportViewer stats line). */
const STATS_LINE_PATTERN = /Based on \d+ questions? \| Avg confidence:\s*[\d.]+%\s*\| \d+ unique sources/i;

export interface ReportContentTabProps {
  reportId: string;
  report: any;
  selectedVersion: string | null;
  onSelectedVersionChange: (v: string) => void;
  reportContent: string;
  reportV1Content: string;
  onReportContentChange: (content: string) => void;
  reportTemplateData?: ReportTemplateResponse | null;
  queryClient: ReturnType<typeof useQueryClient>;
  convertMarkdownToHTML: (markdown: string) => string;
  preprocessMarkdownWithFullRequirements: (markdown: string, template: ReportTemplateResponse | undefined) => string;
  /** When user clicks a citation number [N] in rendered content, jump to Citations & Evidence Trail */
  onCitationClick?: (citationIndex: number) => void;
  /** When true, hide all edit / regenerate / save controls (permission-based read-only). */
  readOnly?: boolean;
  /** When true, show a loading skeleton instead of empty content. */
  isLoading?: boolean;
  /** When true, this report is a proposal (rfp_proposal type) — enables Score Proposal button.
   *  For workplans and other non-proposal report types this button is hidden. */
  isProposalReport?: boolean;
}

/** Exposed so parent can open Edit Content mode and get current displayed content (e.g. for × stats-line remove). */
export interface ReportContentTabRef {
  startEditing: () => void;
  /** Current content being displayed (from editor when editing, else from view). */
  getContent: () => string;
}

export const ReportContentTab = forwardRef<ReportContentTabRef, ReportContentTabProps>(function ReportContentTab({
  reportId,
  report,
  selectedVersion,
  onSelectedVersionChange,
  reportContent,
  reportV1Content,
  onReportContentChange,
  reportTemplateData,
  queryClient,
  convertMarkdownToHTML,
  preprocessMarkdownWithFullRequirements,
  onCitationClick,
  readOnly = false,
  isLoading = false,
  isProposalReport = false,
}, ref) {
  const { addToast } = useToastStore();
  const [isEditing, setIsEditing] = useState(false);
  const [scoreResult, setScoreResult] = useState<ProposalEvaluationResponse | null>(null);
  const [scoreModalOpen, setScoreModalOpen] = useState(false);

  const scoreProposalMutation = useMutation({
    mutationFn: () => scoreReadiness({ reportId }),
    onSuccess: (result) => {
      setScoreResult(result);
      setScoreModalOpen(true);
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Scoring failed', message: err.message });
    },
  });

  React.useEffect(() => {
    if (readOnly) setIsEditing(false);
  }, [readOnly]);
  const [isSaving, setIsSaving] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  /** Live progress for the v2 regen job: percentage 0-100 and/or stage label. */
  const [regenerateProgress, setRegenerateProgress] = useState<
    { percentage: number | null; stage: string | null } | null
  >(null);
  const [regenerateChoiceOpen, setRegenerateChoiceOpen] = useState(false);
  const [feedbackRegenOpen, setFeedbackRegenOpen] = useState(false);

  const pollV2Base = useV2RegenerationJobPoller({
    reportId,
    queryClient,
    addToast,
    report,
    selectedVersion,
    onSelectedVersionChange,
  });

  const pollV2WithChrome = React.useCallback(
    async (newJobId: string, kickoffMessage = 'V2 Regeneration Queued') => {
      setIsRegenerating(true);
      setRegenerateProgress(null);
      try {
        await pollV2Base(newJobId, kickoffMessage, {
          onProgress: (p) => {
            if (p.percentage != null || (p.stage != null && p.stage !== '')) {
              setRegenerateProgress({
                percentage: typeof p.percentage === 'number' ? p.percentage : null,
                stage: typeof p.stage === 'string' ? p.stage : null,
              });
            } else {
              setRegenerateProgress(null);
            }
          },
        });
      } finally {
        setIsRegenerating(false);
        setRegenerateProgress(null);
      }
    },
    [pollV2Base],
  );
  const [viewMode, setViewMode] = useState<'markdown' | 'rendered'>('rendered');
  const [removeTextModalOpen, setRemoveTextModalOpen] = useState(false);
  /** For Remove Text modal: line index → checked (which occurrences to remove). */
  const [removeTextSelected, setRemoveTextSelected] = useState<Record<number, boolean>>({});
  const contentEditAreaRef = useRef<ReportContentEditAreaRef>(null);

  const currentVersion = report?.meta?.current_version || 2;
  const versions: Array<{ value: string; label: string }> = [];
  versions.push({ value: 'v2', label: 'V2 - Executive Summary' });
  versions.push({ value: 'v1', label: 'V1 - Research Draft' });
  if (report?.meta && typeof report.meta === 'object') {
    const meta = report.meta as Record<string, any>;
    const foundVersions = new Set<number>();
    Object.keys(meta).forEach(key => {
      const match = key.match(/^v(\d+)_blob_path$/);
      if (match) foundVersions.add(parseInt(match[1], 10));
    });
    Array.from(foundVersions).filter(v => v >= 3).sort((a, b) => b - a).forEach(v => {
      const changeSummary = meta[`v${v}_change_summary`] || 'Manual edit';
      versions.unshift({ value: `v${v}`, label: `V${v} - ${changeSummary.substring(0, 30)}${changeSummary.length > 30 ? '...' : ''}` });
    });
    for (let v = 3; v <= currentVersion; v++) {
      if (!versions.some(vv => vv.value === `v${v}`)) versions.unshift({ value: `v${v}`, label: `V${v} - Manual edit via editor` });
    }
  }
  versions.sort((a, b) => (parseInt(b.value.slice(1), 10) || 0) - (parseInt(a.value.slice(1), 10) || 0));

  const activeContent = useMemo(() => {
    const raw = selectedVersion === 'v1' ? reportV1Content : reportContent;
    return raw || '';
  }, [selectedVersion, reportV1Content, reportContent]);

  useImperativeHandle(ref, () => ({
    startEditing: () => setIsEditing(true),
    getContent: () => contentEditAreaRef.current?.getContent() ?? activeContent,
  }), [activeContent]);

  /** Find all lines matching the stats pattern; returns { lineIndex, text }[]. */
  const statsLineMatches = useMemo(() => {
    if (!removeTextModalOpen) return [];
    const content = isEditing
      ? (contentEditAreaRef.current?.getContent() ?? activeContent)
      : activeContent;
    const lines = content.split('\n');
    const out: { lineIndex: number; text: string }[] = [];
    lines.forEach((line, i) => {
      if (STATS_LINE_PATTERN.test(line.trim())) {
        out.push({ lineIndex: i, text: line.trim() });
      }
    });
    return out;
  }, [removeTextModalOpen, activeContent, isEditing]);

  /** When opening Remove Text modal, select all matches by default. */
  const openRemoveTextModal = () => {
    setRemoveTextModalOpen(true);
  };
  const closeRemoveTextModal = () => {
    setRemoveTextModalOpen(false);
    setRemoveTextSelected({});
  };

  /**
   * After removing stats lines: update local content, close modal, save to server (create version).
   * Editor is not opened; user stays in rendered view. Cancel in the modal does not change content.
   */
  const saveNewContentAfterRemove = async (newContent: string) => {
    onReportContentChange(newContent);
    if (isEditing) contentEditAreaRef.current?.setContent(newContent);
    closeRemoveTextModal();
    try {
      setIsSaving(true);
      const { saveReportContent } = await import('../../api/reports');
      const result = await saveReportContent(reportId, newContent, 'v2');
      onReportContentChange(newContent);
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      if (result?.version) {
        onSelectedVersionChange(String(result.version));
      } else if (result?.current_version != null) {
        onSelectedVersionChange(`v${result.current_version}`);
      }
      alert(`Stats lines removed and saved as ${result.version} successfully.`);
    } catch (error: any) {
      console.error('Failed to save after remove:', error);
      alert(`Removal applied locally but save failed: ${error.message || 'Unknown error'}.`);
    } finally {
      setIsSaving(false);
    }
  };

  /** Remove selected stats lines, save (create version), then open editor. */
  const applyRemoveSelected = async () => {
    const content = isEditing
      ? (contentEditAreaRef.current?.getContent() ?? activeContent)
      : activeContent;
    const lines = content.split('\n');
    const toRemove = new Set(
      statsLineMatches.filter((m) => removeTextSelected[m.lineIndex] !== false).map((m) => m.lineIndex)
    );
    if (toRemove.size === 0) return;
    const newLines = lines.filter((_, i) => !toRemove.has(i));
    const newContent = newLines.join('\n');
    await saveNewContentAfterRemove(newContent);
  };

  /** Remove all stats lines, save (create version). */
  const applyRemoveAll = async () => {
    const content = isEditing
      ? (contentEditAreaRef.current?.getContent() ?? activeContent)
      : activeContent;
    const newContent = content
      .split('\n')
      .filter((line) => !STATS_LINE_PATTERN.test(line.trim()))
      .join('\n');
    await saveNewContentAfterRemove(newContent);
  };

  const toggleRemoveLine = (lineIndex: number) => {
    setRemoveTextSelected((prev) => ({ ...prev, [lineIndex]: !(prev[lineIndex] !== false) }));
  };
  /** Checked = will be removed; default (not in map) = selected/checked. */
  const isLineSelectedForRemove = (lineIndex: number) => removeTextSelected[lineIndex] !== false;
  const selectAllRemove = () => {
    const next: Record<number, boolean> = {};
    statsLineMatches.forEach((m) => { next[m.lineIndex] = true; });
    setRemoveTextSelected(next);
  };
  const selectNoneRemove = () => {
    const next: Record<number, boolean> = {};
    statsLineMatches.forEach((m) => { next[m.lineIndex] = false; });
    setRemoveTextSelected(next);
  };
  const selectedCount = statsLineMatches.filter((m) => isLineSelectedForRemove(m.lineIndex)).length;

  const handleSaveContent = async () => {
    const content = contentEditAreaRef.current?.getContent();
    if (content === undefined) return;
    try {
      setIsSaving(true);
      const { saveReportContent } = await import('../../api/reports');
      const result = await saveReportContent(reportId, content, 'v2');
      onReportContentChange(content);
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      if (result?.version) {
        onSelectedVersionChange(String(result.version));
      } else if (result?.current_version != null) {
        onSelectedVersionChange(`v${result.current_version}`);
      }
      alert(`Report content saved as ${result.version} successfully`);
    } catch (error: any) {
      console.error('Failed to save report content:', error);
      alert(`Failed to save: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <React.Fragment>
    <Card>
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b border-gray-200 pb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={selectedVersion || `v${currentVersion}`}
              onChange={(e) => onSelectedVersionChange(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
            >
              {versions.map(v => (
                <option key={v.value} value={v.value}>{v.label}</option>
              ))}
            </select>
            <div className="h-6 w-px bg-gray-300 mx-1" />
            <button
              type="button"
              onClick={() => setViewMode('rendered')}
              className={`px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors ${viewMode === 'rendered' ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
            >
              <Eye className="h-4 w-4" />
              Rendered
            </button>
          </div>
          {!readOnly && !isEditing ? (
            <div className="flex items-center gap-2 md:gap-4 flex-wrap">
              {((selectedVersion && (selectedVersion === 'v2' || parseInt(selectedVersion.slice(1), 10) >= 2)) || !selectedVersion) && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setRegenerateChoiceOpen(true)}
                  disabled={isRegenerating}
                  title="Choose standard V2 regen or one that threads QA gaps and comments into the prompts"
                >
                  <RefreshCw className={`h-4 w-4 ${isRegenerating ? 'animate-spin' : ''}`} />
                  <span className="hidden md:inline ml-2">
                    {isRegenerating
                      ? regenerateProgress?.percentage != null
                        ? `Regenerating V2 (${Math.round(regenerateProgress.percentage)}%)`
                        : regenerateProgress?.stage
                          ? `Regenerating V2 — ${regenerateProgress.stage}`
                          : 'Regenerating V2…'
                      : 'Regenerate V2'}
                  </span>
                </Button>
              )}
              {isProposalReport && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => scoreProposalMutation.mutate()}
                disabled={scoreProposalMutation.isPending}
                title="Score this proposal against the technical evaluation rubric"
              >
                {scoreProposalMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Target className="h-4 w-4" />
                )}
                <span className="hidden md:inline ml-2">
                  {scoreProposalMutation.isPending ? 'Scoring…' : 'Score Proposal'}
                </span>
              </Button>
              )}
              <Button size="sm" onClick={() => setIsEditing(true)}>
                <Edit className="h-4 w-4" />
                <span className="hidden md:inline ml-2">Edit Content</span>
              </Button>
              <Button size="sm" onClick={openRemoveTextModal}>
                <Trash2 className="h-4 w-4" />
                <span className="hidden md:inline ml-2">Remove AI disclaimers</span>
              </Button>
            </div>
          ) : !readOnly ? (
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  contentEditAreaRef.current?.setContent(activeContent);
                  setIsEditing(false);
                }}
              >
                <XIcon className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveContent} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          ) : null}
        </div>
        {isLoading || (!activeContent && !isEditing) ? (
          <div className="space-y-3 py-4 animate-pulse" aria-label="Loading report content">
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/5" />
            <div className="mt-4 h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
          </div>
        ) : (
          <ReportContentEditArea
            ref={contentEditAreaRef}
            initialContent={activeContent}
            isEditing={readOnly ? false : isEditing}
            viewMode={viewMode}
            reportTemplateData={reportTemplateData ?? null}
            selectedVersion={selectedVersion}
            convertMarkdownToHTML={convertMarkdownToHTML}
            preprocessMarkdownWithFullRequirements={preprocessMarkdownWithFullRequirements}
            onCitationClick={onCitationClick}
          />
        )}
      </div>

      {/* Regenerate mode chooser → standard poll or QA feedback-plan path */}
      {regenerateChoiceOpen && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-label="Choose V2 regeneration mode"
          onClick={() => setRegenerateChoiceOpen(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Regenerate executive summary (V2)
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                Run a standard pass, or compile QA evaluation gaps plus comments /
                revisions into reviewer tips before regenerating.&nbsp;
                Scoring stays on the <strong className="text-gray-700 dark:text-gray-300">QA</strong> tab.
              </p>
            </div>
            <div className="p-5 flex flex-col gap-3">
              <Button
                type="button"
                className="w-full justify-center"
                onClick={async () => {
                  setRegenerateChoiceOpen(false);
                  try {
                    const { regenerateReportSections } =
                      await import('../../api/reports');
                    const jobResult = await regenerateReportSections(reportId, []);
                    if (!jobResult?.id) {
                      throw new Error('Regeneration did not return a job id');
                    }
                    await pollV2WithChrome(String(jobResult.id), 'V2 Regeneration Queued');
                  } catch (error: unknown) {
                    addToast({
                      type: 'error',
                      title: 'Failed to Start V2 Regeneration',
                      message:
                        error instanceof Error
                          ? error.message
                          : 'Could not queue regeneration job',
                    });
                  }
                }}
              >
                Standard regenerate
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full justify-center"
                onClick={() => {
                  setRegenerateChoiceOpen(false);
                  setFeedbackRegenOpen(true);
                }}
              >
                Use QA evaluation & comments →
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full justify-center"
                onClick={() => setRegenerateChoiceOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      <FeedbackPlanPreviewModal
        reportId={reportId}
        open={feedbackRegenOpen}
        onClose={() => setFeedbackRegenOpen(false)}
        autoComputeOnOpen
        expandPromptPreviewOnOpen={false}
        onEmbeddedRegenerationComplete={() => {
          const fresh = queryClient.getQueryData(['report', reportId]) as
            | ReportContentTabProps['report']
            | undefined;
          const cv = fresh?.meta?.current_version;
          if (cv != null) onSelectedVersionChange(`v${cv}`);
          else if (selectedVersion?.startsWith?.('v')) {
            const n = parseInt(selectedVersion.slice(1), 10);
            if (!Number.isNaN(n)) onSelectedVersionChange(`v${n + 1}`);
          }
        }}
      />

      {/* Remove Text modal: list stats lines, single/multiple remove */}
      {removeTextModalOpen && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50" onClick={closeRemoveTextModal}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Remove AI Disclaimers</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                After reviewing the AI generated content &quot;Based on N questions | Avg confidence: X% | Y unique sources&quot;, select which to remove, then confirm.
              </p>
            </div>
            <div className="p-4 overflow-y-auto flex-1 min-h-0">
              {statsLineMatches.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400">No matching lines found in the current content.</p>
              ) : (
                <>
                  <div className="flex gap-2 mb-3">
                    <button type="button" onClick={selectAllRemove} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">Select all</button>
                    <span className="text-gray-400">|</span>
                    <button type="button" onClick={selectNoneRemove} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">Select none</button>
                  </div>
                  <ul className="space-y-2">
                    {statsLineMatches.map(({ lineIndex, text }) => (
                      <li key={lineIndex} className="flex items-start gap-3 p-2 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                        <input
                          type="checkbox"
                          checked={isLineSelectedForRemove(lineIndex)}
                          onChange={() => toggleRemoveLine(lineIndex)}
                          className="mt-1 rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-800 dark:text-gray-200 break-all">Line {lineIndex + 1}: {text}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={closeRemoveTextModal} disabled={isSaving}>Cancel</Button>
              {statsLineMatches.length > 0 && (
                <>
                  <Button size="sm" onClick={applyRemoveSelected} disabled={selectedCount === 0 || isSaving}>
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving…
                      </>
                    ) : (
                      `Yes — Remove selected (${selectedCount})`
                    )}
                  </Button>
                  <Button size="sm" onClick={applyRemoveAll} disabled={isSaving}>
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving…
                      </>
                    ) : (
                      `Remove all (${statsLineMatches.length})`
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </Card>

      {/* Score Results Modal — shown after "Score Proposal" completes */}
      {scoreModalOpen && scoreResult && createPortal(
        <div
          className="fixed inset-0 z-[200] flex items-start justify-center bg-black/60 overflow-y-auto py-8 px-4"
          onClick={() => setScoreModalOpen(false)}
        >
          <div
            className="relative w-[min(900px,98vw)] rounded-xl border border-slate-200 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-slate-900">Score Readiness</h2>
              <button
                type="button"
                className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                onClick={() => setScoreModalOpen(false)}
                aria-label="Close"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <QAScoreReadinessPanel
                reportId={reportId}
                initialData={scoreResult}
                executiveSummaryVersionLabel={selectedVersion ?? undefined}
                onBelowTargetFeedbackStep1={() => {
                  setScoreModalOpen(false);
                  setFeedbackRegenOpen(true);
                }}
                onBelowTargetFeedbackStep2={() => {
                  setScoreModalOpen(false);
                  setFeedbackRegenOpen(true);
                }}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </React.Fragment>
  );
});
